'use client';

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Check,
  Download,
  Globe2,
  Info,
  MonitorDown,
  Star,
  ArrowRight,
  Zap,
  BarChart3,
  Smartphone,
  Shield,
  Users,
  TrendingUp,
  CreditCard,
  Package,
  Settings,
  Truck,
} from 'lucide-react';
import { useRouter } from 'next/navigation';
import { supabase } from '@/lib/supabase';

// ================== Background particles ==================
const ParticleBackground = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-40 -right-32 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl" />
      <div className="absolute -bottom-40 -left-32 w-80 h-80 bg-blue-500/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />
    </div>
  );
};

// ================== Plan card ==================
type PlanCardProps = {
  plan: string;
  price: string;
  features: string[];
  popular?: boolean;
  delay?: number;
};

const PlanCard: React.FC<PlanCardProps> = ({ plan, price, features, popular, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      whileHover={{ y: -10, scale: 1.02 }}
      className={`relative rounded-2xl p-8 backdrop-blur-xl border ${
        popular
          ? 'border-purple-500/50 bg-gradient-to-br from-purple-500/10 to-blue-500/10 shadow-lg shadow-purple-500/20'
          : 'border-white/10 bg-white/5'
      } transition-all duration-500 overflow-hidden`}
    >
      {popular && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
          Recomendado
        </div>
      )}

      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          {plan}
        </h3>
        <div className="mt-4">
          <span className="text-4xl font-bold">${price}</span>
          <span className="text-gray-400">/mes</span>
        </div>
      </div>

      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <Check className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" />
            <span className="text-gray-300">{feature}</span>
          </li>
        ))}
      </ul>

      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className={`w-full py-3 rounded-xl font-semibold transition-all duration-300 ${
          popular
            ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white shadow-lg shadow-purple-500/30'
            : 'bg-white/10 text-white border border-white/20 hover:bg-white/20'
        }`}
      >
        Comenzar Ahora
      </motion.button>
    </motion.div>
  );
};


// ================== Free plan row ==================
type FreePlanRowProps = {
  plan: string;
  price: string;
  features: string[];
  delay?: number;
};

const FreePlanRow: React.FC<FreePlanRowProps> = ({ plan, price, features, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay }}
      viewport={{ once: true }}
      whileHover={{ y: -6, scale: 1.01 }}
      className="relative overflow-hidden rounded-3xl border border-emerald-400/30 bg-gradient-to-br from-emerald-500/15 via-white/5 to-blue-500/10 backdrop-blur-xl p-6 md:p-8 shadow-lg shadow-emerald-500/10"
    >
      {/* glow */}
      <div className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-emerald-500/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-blue-500/15 blur-3xl" />

      <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
        <div className="min-w-[240px]">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-200">
            <Zap className="h-4 w-4" />
            Plan gratuito
          </div>

          <h3 className="mt-4 text-3xl md:text-4xl font-extrabold tracking-tight bg-gradient-to-r from-white to-gray-200 bg-clip-text text-transparent">
            {plan}
          </h3>

          <div className="mt-3 flex items-end gap-2">
            <span className="text-5xl md:text-6xl font-extrabold">${price}</span>
            <span className="pb-2 text-gray-300 text-sm">/mes</span>
          </div>

          <p className="mt-3 text-gray-300 max-w-md">
            Empieza a vender y llevar tu catálogo sin costo. Cuando crezcas, desbloquea módulos avanzados.
          </p>

          <motion.button
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="mt-5 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-400 px-5 py-3 font-semibold text-white shadow-lg shadow-emerald-500/25"
          >
            Empezar gratis
            <ArrowRight className="h-5 w-5" />
          </motion.button>
        </div>

        <div className="flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
            {features.map((feature, index) => (
              <div
                key={index}
                className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/5 p-4"
              >
                <Check className="h-5 w-5 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span className="text-gray-200">{feature}</span>
              </div>
            ))}
          </div>

          <div className="mt-4 text-xs text-gray-400">
            Nota: El plan FREE incluye 1 almacén interno solo para descontar stock al vender (sin gestión).
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ================== Testimonial card ==================
type TestimonialCardProps = {
  name: string;
  role: string;
  content: string;
  rating: number;
};

const TestimonialCard: React.FC<TestimonialCardProps> = ({ name, role, content, rating }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6"
    >
      <div className="flex mb-4">
        {[...Array(5)].map((_, i) => (
          <Star
            key={i}
            className={`h-5 w-5 ${i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-600'}`}
          />
        ))}
      </div>
      <p className="text-gray-300 mb-6 italic">"{content}"</p>
      <div>
        <p className="font-semibold text-white">{name}</p>
        <p className="text-gray-400 text-sm">{role}</p>
      </div>
    </motion.div>
  );
};

// ================== Integration logo ==================
const IntegrationLogo: React.FC<{ name: string }> = ({ name }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      whileHover={{ scale: 1.1 }}
      className="flex items-center justify-center h-20 w-40 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:border-purple-500/50 transition-all duration-300"
    >
      <span className="text-white font-semibold text-lg">{name}</span>
    </motion.div>
  );
};

// ================== Static data ==================
const FEATURES = [
  {
    icon: <Smartphone className="h-10 w-10" />,
    title: 'Acceso Multiplataforma',
    description: 'Usa POS Modular en cualquier dispositivo: computadora, tablet o smartphone.',
  },
  {
    icon: <BarChart3 className="h-10 w-10" />,
    title: 'Reportes en Tiempo Real',
    description: 'Visualiza tus ventas, inventario y métricas financieras al instante.',
  },
  {
    icon: <Zap className="h-10 w-10" />,
    title: 'Rendimiento Optimizado',
    description: 'Tecnología de vanguardia para una experiencia rápida y fluida.',
  },
  {
    icon: <Shield className="h-10 w-10" />,
    title: 'Seguridad Avanzada',
    description: 'Tus datos están protegidos con encriptación de última generación.',
  },
];

const PLANS = [
  {
    name: 'FREE',
    price: '0',
    features: [
      'Ventas',
      'Productos',
      '1 almacén interno (sin gestión)',
      'Sin caja, configuración, reportes, almacén (gestión), transferencias, conteos ni cotizaciones',
    ],
  },
  {
    name: 'Operación',
    price: '299',
    features: [
      'Caja (cash)',
      'Configuración (settings): branding, usuarios ilimitados, roles/permisos y editor de tickets/formatos de impresión',
      'Reportes (reports)',
      'Almacén básico + transferencias (warehouse_basic, warehouse_transfers)',
      'Cotizaciones básicas: crear/ver/imprimir/exportar (quotes_basic)',
      'Sin convertir a venta, sin proveedores, sin conteos y sin estatus/pipeline de cotizaciones',
    ],
  },
  {
    name: 'Profesional',
    price: '499',
    features: [
      'Todo lo de Operación',
      'Proveedores (suppliers)',
      'Convertir cotización a venta (quotes_convert)',
      'La venta se crea con referencia al folio/ID de la cotización (quote_folio / source_quote_id)',
      'Sin CRM de cotizaciones (estatus/pipeline) y sin conteos',
    ],
    popular: true,
  },
  {
    name: 'Empresarial',
    price: '699',
    features: [
      'Todo lo de Profesional',
      'Conteos (warehouse_counts) — CountsAudit completo',
      'CRM de cotizaciones (quotes_crm): estatus/pipeline, seguimiento y vencimiento',
    ],
  },
];

const FREE_PLAN = PLANS.find((p) => p.name === 'FREE');
const PAID_PLANS = PLANS.filter((p) => p.name !== 'FREE');


const MODULES = [
  { name: 'Ventas', icon: <CreditCard className="h-6 w-6" /> },
  { name: 'Caja', icon: <TrendingUp className="h-6 w-6" /> },
  { name: 'Reportes', icon: <BarChart3 className="h-6 w-6" /> },
  { name: 'Almacén', icon: <Package className="h-6 w-6" /> },
  { name: 'Productos', icon: <Zap className="h-6 w-6" /> },
  { name: 'Proveedores', icon: <Truck className="h-6 w-6" /> },
  { name: 'Configuración', icon: <Settings className="h-6 w-6" /> },
  { name: 'Usuarios', icon: <Users className="h-6 w-6" /> },
];

const INTEGRATIONS = [
  'Shopify',
  'WooCommerce',
  'Stripe',
  'PayPal',
  'QuickBooks',
  'Xero',
  'Mailchimp',
  'Zapier',
  'Google Analytics',
  'Slack',
];

const TESTIMONIALS = [
  {
    name: 'María González',
    role: 'Dueña de Boutique Elegante',
    content:
      'POS Modular transformó completamente mi negocio. La facilidad para gestionar inventario y ventas es increíble.',
    rating: 5,
  },
  {
    name: 'Carlos Rodríguez',
    role: 'Gerente de Restaurante Sazón',
    content:
      'El sistema de reportes en tiempo real nos ha permitido tomar decisiones más informadas y aumentar nuestras ganancias.',
    rating: 5,
  },
  {
    name: 'Ana Martínez',
    role: 'Propietaria de Tienda Tech',
    content:
      'La integración con otras plataformas ha simplificado nuestro flujo de trabajo. ¡Altamente recomendado!',
    rating: 4,
  },
];

// ===== Boot sequence steps for welcome screen =====
const BOOT_STEPS = [
  {
    label: 'Sesión activa',
    detail: 'Usuario conectado correctamente.',
    icon: <Users className="h-3.5 w-3.5" />,
  },
  {
    label: 'Autenticación verificada',
    detail: 'Credenciales y token validados.',
    icon: <Shield className="h-3.5 w-3.5" />,
  },
  {
    label: 'Conexión segura',
    detail: 'Canal cifrado TLS establecido.',
    icon: <Shield className="h-3.5 w-3.5" />,
  },
  {
    label: 'Datos operativos',
    detail: 'Ventas y caja sincronizadas.',
    icon: <BarChart3 className="h-3.5 w-3.5" />,
  },
  {
    label: 'Inventario actualizado',
    detail: 'Existencias cargadas por almacén.',
    icon: <Package className="h-3.5 w-3.5" />,
  },
  {
    label: 'Métricas preparadas',
    detail: 'KPIs listos para visualizar.',
    icon: <TrendingUp className="h-3.5 w-3.5" />,
  },
];

const BOOT_STEP_INTERVAL = 650; // ms
const BOOT_EXTRA_DELAY = 700; // ms extra al final
const BOOT_TOTAL_DURATION_MS = BOOT_STEPS.length * BOOT_STEP_INTERVAL + BOOT_EXTRA_DELAY;
const BOOT_TOTAL_DURATION_SEC = BOOT_TOTAL_DURATION_MS / 1000;
const WINDOWS_DOWNLOAD_URL = '/downloads/ryupos-windows.exe';

// ================== Main component ==================
const POSModularLanding: React.FC = () => {
  const router = useRouter();

  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const [checkingSession, setCheckingSession] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [isDesktopApp, setIsDesktopApp] = useState(false);

  const [currentBootStep, setCurrentBootStep] = useState(0);

  // session check
  useEffect(() => {
    const checkSessionAndHandle = async () => {
      try {
        const desktopApp = typeof window !== 'undefined' && Boolean((window as any).ryuposDesktop);
        setIsDesktopApp(desktopApp);

        if (desktopApp && window.location.pathname === '/') {
          router.replace('/venta');
          return;
        }

        const { data, error } = await supabase.auth.getSession();
        if (error) {
          console.error('Error getting session:', error);
          setCheckingSession(false);
          return;
        }

        if (data?.session) {
          setHasSession(true);
          setUserEmail(data.session.user.email ?? null);
          setShowWelcome(true);
          setCheckingSession(false);

          // redirección sincronizada con la animación de pasos
          setTimeout(() => {
            setShowWelcome(false);
            router.replace('/venta');
          }, BOOT_TOTAL_DURATION_MS);

          return;
        }

        setHasSession(false);
        setCheckingSession(false);
      } catch (err) {
        console.error('Unexpected error getting session:', err);
        setCheckingSession(false);
      }
    };

    checkSessionAndHandle();
  }, [router]);

  // testimonials carousel
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // boot sequence: avanzar pasos uno por uno
  useEffect(() => {
    if (!hasSession || !showWelcome) return;

    setCurrentBootStep(0);
    let step = 0;

    const intervalId = setInterval(() => {
      step += 1;
      if (step >= BOOT_STEPS.length) {
        clearInterval(intervalId);
        return;
      }
      setCurrentBootStep(step);
    }, BOOT_STEP_INTERVAL);

    return () => clearInterval(intervalId);
  }, [hasSession, showWelcome]);

  // ========== loading while checking session ==========
  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-950 via-black to-gray-900 text-white relative overflow-hidden">
        <ParticleBackground />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(168,85,247,0.18),_transparent_55%),radial-gradient(circle_at_bottom,_rgba(56,189,248,0.18),_transparent_55%)]" />
        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="h-10 w-10 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" />
          <p className="text-gray-300 text-sm">Preparando tu experiencia...</p>
        </div>
      </div>
    );
  }

  // ========== welcome screen with boot sequence ==========
  if (hasSession && showWelcome) {
    return (
      <div className="min-h-screen relative flex items-center justify-center bg-gradient-to-br from-[#050816] via-[#050012] to-black text-white overflow-hidden">
        {/* Deep background layers */}
        <ParticleBackground />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(129,140,248,0.24),_transparent_60%)]" />
        <div className="absolute inset-x-0 -bottom-40 h-80 opacity-60 bg-[radial-gradient(circle_at_bottom,_rgba(236,72,153,0.35),_transparent_55%)]" />

        {/* border glow */}
        <div className="pointer-events-none absolute inset-20 rounded-[3rem] border border-white/5 shadow-[0_0_80px_rgba(129,140,248,0.35)]" />

        {/* subtle grid */}
        <div className="pointer-events-none absolute inset-0 opacity-[0.12]">
          <div className="h-full w-full bg-[linear-gradient(to_right,_#1e1e24_1px,transparent_1px),linear-gradient(to_bottom,_#1e1e24_1px,transparent_1px)] bg-[size:60px_60px]" />
        </div>

        {/* MAIN CONTENT */}
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.7, ease: 'easeOut' }}
          className="relative z-10 max-w-5xl w-full px-6 md:px-10"
        >
          <div className="flex flex-col gap-10 md:flex-row md:items-center md:justify-between">
            {/* LEFT SIDE – main message */}
            <div className="flex-1 flex flex-col items-center md:items-start text-center md:text-left">
              {/* tags */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
                className="mb-6 flex flex-wrap items-center justify-center md:justify-start gap-3"
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-violet-400/40 bg-violet-500/10 px-4 py-1.5 text-[11px] tracking-[0.22em] uppercase text-violet-100">
                  <Zap className="h-3 w-3 text-violet-300" />
                  <span>POS Modular</span>
                </div>
                <div className="inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-500/10 px-3 py-1 text-[11px] uppercase tracking-[0.22em] text-sky-100">
                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
                  Sesión activa
                </div>
              </motion.div>

              {/* headline */}
              <motion.h1
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.18, duration: 0.6 }}
                className="text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4 leading-tight"
              >
                <span className="block bg-gradient-to-r from-white via-purple-200 to-sky-300 bg-clip-text text-transparent">
                  Bienvenido de nuevo
                </span>
                <span className="mt-1 block text-base sm:text-lg font-normal text-gray-300">
                  Estamos arrancando tu punto de venta como si fuera una PC de alto rendimiento.
                </span>
              </motion.h1>

              {/* user + description */}
              {userEmail && (
                <motion.p
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.28, duration: 0.5 }}
                  className="text-sm sm:text-base text-gray-300 mb-2"
                >
                  Sesión iniciada como{' '}
                  <span className="font-semibold text-violet-200">{userEmail}</span>
                </motion.p>
              )}

              <motion.p
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.32, duration: 0.5 }}
                className="max-w-xl text-xs sm:text-sm text-gray-400 mb-8"
              >
                Cargaremos tus módulos uno por uno: autenticación, seguridad, datos, inventario y
                métricas. En cuanto todo esté en verde, te enviaremos directo a la pantalla de
                Ventas.
              </motion.p>

              {/* status cards */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.38, duration: 0.5 }}
                className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full mb-8"
              >
                <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 backdrop-blur-xl">
                  <BarChart3 className="h-5 w-5 text-sky-300" />
                  <div className="text-left">
                    <p className="text-xs text-gray-400">Próximo módulo</p>
                    <p className="text-sm font-semibold text-white">Panel de ventas</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 backdrop-blur-xl">
                  <Package className="h-5 w-5 text-emerald-300" />
                  <div className="text-left">
                    <p className="text-xs text-gray-400">Inventario</p>
                    <p className="text-sm font-semibold text-white">Sincronizando existencias</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl bg-white/5 border border-white/10 px-4 py-3 backdrop-blur-xl">
                  <Shield className="h-5 w-5 text-violet-300" />
                  <div className="text-left">
                    <p className="text-xs text-gray-400">Seguridad</p>
                    <p className="text-sm font-semibold text-white">Sesión cifrada</p>
                  </div>
                </div>
              </motion.div>

              {/* progress bar */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.5 }}
                className="w-full max-w-md"
              >
                <div className="h-1.5 w-full rounded-full bg-white/10 overflow-hidden mb-3">
                  <motion.div
                    initial={{ width: '0%' }}
                    animate={{ width: '100%' }}
                    transition={{ duration: BOOT_TOTAL_DURATION_SEC, ease: 'easeInOut' }}
                    className="h-full w-full bg-gradient-to-r from-violet-500 via-sky-400 to-violet-500 shadow-[0_0_25px_rgba(129,140,248,0.8)]"
                  />
                </div>
                <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-gray-500">
                  <span>Inicializando módulos del POS…</span>
                  <span className="flex items-center gap-1">
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400 animate-ping" />
                    <span className="inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  </span>
                </div>
              </motion.div>
            </div>

            {/* RIGHT SIDE – boot console */}
            <motion.div
              initial={{ opacity: 0, x: 25 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.25, duration: 0.6 }}
              className="mt-10 md:mt-0 flex-1 flex justify-center md:justify-end"
            >
              <div className="relative w-full max-w-sm">
                {/* neon blur */}
                <div className="absolute -top-10 -right-6 h-24 w-24 rounded-full bg-gradient-to-br from-purple-500/50 via-sky-400/40 to-transparent blur-3xl opacity-80" />

                <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-slate-950/90 via-purple-950/40 to-slate-900/80 p-5 backdrop-blur-2xl shadow-[0_0_60px_rgba(15,23,42,0.9)] font-mono text-[11px] text-gray-300">
                  {/* header */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-gradient-to-br from-violet-500 to-sky-500 shadow-[0_0_18px_rgba(129,140,248,0.8)]">
                        <CreditCard className="h-3.5 w-3.5 text-white" />
                      </div>
                      <div className="text-left">
                        <p className="text-[10px] text-gray-400">Destino</p>
                        <p className="text-xs font-semibold text-white">/modules/venta</p>
                      </div>
                    </div>
                    <span className="rounded-full border border-emerald-400/40 bg-emerald-500/10 px-3 py-1 text-[10px] font-medium text-emerald-200">
                      ready • secure
                    </span>
                  </div>

                  {/* console lines */}
                  <div className="relative mt-2 space-y-2">
                    {BOOT_STEPS.map((step, index) => {
                      const isCompleted = index < currentBootStep;
                      const isActive = index === currentBootStep;

                      return (
                        <div
                          key={step.label}
                          className="flex items-start gap-2 rounded-lg px-2 py-1.5 transition-colors duration-200"
                        >
                          <div className="mt-0.5">
                            {isCompleted ? (
                              <span className="text-emerald-400">[OK]</span>
                            ) : isActive ? (
                              <span className="text-sky-300">[···]</span>
                            ) : (
                              <span className="text-gray-600">[--]</span>
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span
                                className={
                                  isCompleted
                                    ? 'text-emerald-300'
                                    : isActive
                                    ? 'text-sky-200'
                                    : 'text-gray-400'
                                }
                              >
                                {step.icon}
                              </span>
                              <span
                                className={
                                  isCompleted
                                    ? 'text-emerald-300'
                                    : isActive
                                    ? 'text-sky-100'
                                    : 'text-gray-400'
                                }
                              >
                                {step.label}
                              </span>
                              {isCompleted && (
                                <span className="ml-1 rounded-full border border-emerald-500/50 bg-emerald-500/10 px-2 py-[1px] text-[9px] uppercase tracking-[0.18em] text-emerald-300">
                                  listo
                                </span>
                              )}
                              {isActive && (
                                <span className="ml-1 rounded-full border border-sky-500/50 bg-sky-500/10 px-2 py-[1px] text-[9px] uppercase tracking-[0.18em] text-sky-200">
                                  cargando…
                                </span>
                              )}
                            </div>
                            <p className="mt-0.5 text-[10px] text-gray-500">
                              {step.detail}
                            </p>
                          </div>
                        </div>
                      );
                    })}

                    {/* final line */}
                    <div className="mt-3 flex items-center gap-2 px-2 pt-2 border-t border-white/10 text-[10px] text-gray-400">
                      <span className="text-purple-300">pos@modular</span>
                      <span className="text-gray-600">:</span>
                      <span className="text-sky-300">~/boot</span>
                      <span className="text-gray-500">$</span>
                      <span className="text-gray-300"> redirect /modules/venta</span>
                      <span className="inline-block h-3 w-1 bg-sky-200 animate-pulse" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ========== landing (no session) ==========
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white overflow-x-hidden">
      <ParticleBackground />

      {/* Hero */}
      <section className="relative min-h-screen flex items-center justify-center px-4 pt-20">
        <div className="max-w-7xl mx-auto text-center z-10">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-5xl md:text-7xl font-bold mb-6"
          >
            <span className="bg-gradient-to-r from-white via-purple-200 to-blue-300 bg-clip-text text-transparent">
              El POS más completo
            </span>
            <br />
            <span className="bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent">
              para tu negocio
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto mb-10"
          >
            Gestiona ventas, inventario y reportes desde cualquier lugar con nuestro sistema
            modular y escalable.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center items-center"
          >
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => router.push('/venta')}
              className="px-8 py-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl font-semibold text-lg shadow-lg shadow-purple-500/30 flex items-center gap-2"
            >
              Probar Gratis <ArrowRight className="h-5 w-5" />
            </motion.button>

            {!isDesktopApp && (
              <motion.a
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                href={WINDOWS_DOWNLOAD_URL}
                download
                className="relative px-8 py-4 bg-white text-slate-950 rounded-xl font-semibold text-lg shadow-lg shadow-blue-500/20 flex items-center gap-2"
              >
                <Download className="h-5 w-5" />
                Descargar Windows
              </motion.a>
            )}

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-8 py-4 border border-white/20 rounded-xl font-semibold text-lg backdrop-blur-sm hover:bg-white/10 transition-all duration-300"
            >
              Ver Planes
            </motion.button>
          </motion.div>

          {!isDesktopApp && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mt-5 flex flex-col items-center gap-3 text-sm text-gray-300"
            >
              <p className="max-w-3xl">
                Usa Ryupos desde la web sin instalar nada, o instala la app de Windows para mayor compatibilidad en caja.
              </p>

              <div className="flex flex-col sm:flex-row flex-wrap items-center justify-center gap-2">
                <div className="group relative inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-2 backdrop-blur-sm">
                  <Globe2 className="h-4 w-4 text-sky-300" />
                  <span>Web sin instalaciones</span>
                  <Info className="h-4 w-4 text-gray-400" />
                  <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-64 -translate-x-1/2 rounded-xl border border-white/10 bg-slate-950/95 p-3 text-left text-xs text-gray-200 opacity-0 shadow-xl transition-opacity group-hover:opacity-100">
                    Entra desde cualquier navegador. Ideal si solo quieres probar o trabajar desde cualquier equipo.
                  </div>
                </div>

                <div className="group relative inline-flex items-center gap-2 rounded-full border border-blue-400/25 bg-blue-500/10 px-3 py-2 backdrop-blur-sm">
                  <MonitorDown className="h-4 w-4 text-blue-300" />
                  <span>App instalada</span>
                  <Info className="h-4 w-4 text-gray-400" />
                  <div className="pointer-events-none absolute left-1/2 top-full z-20 mt-2 w-72 -translate-x-1/2 rounded-xl border border-white/10 bg-slate-950/95 p-3 text-left text-xs text-gray-200 opacity-0 shadow-xl transition-opacity group-hover:opacity-100">
                    Mejor experiencia en Windows: ventana propia, acceso desde escritorio y base para funciones más compatibles con impresoras.
                  </div>
                </div>

                <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/25 bg-emerald-500/10 px-3 py-2 text-emerald-200 backdrop-blur-sm">
                  <Shield className="h-4 w-4" />
                  <span>Descarga segura</span>
                </div>
              </div>
            </motion.div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="mt-16 relative"
          >
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-2xl blur-xl" />
            <div className="relative bg-gray-800/30 backdrop-blur-xl border border-white/10 rounded-2xl p-4 md:p-8">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {MODULES.map((module, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                    whileHover={{ scale: 1.05, y: -5 }}
                    className="flex flex-col items-center justify-center p-4 bg-white/5 rounded-xl border border-white/10 hover:border-purple-500/50 transition-all duration-300"
                  >
                    <div className="text-purple-400 mb-2">{module.icon}</div>
                    <span className="text-sm font-medium">{module.name}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Potencia tu negocio
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Descubre todas las funcionalidades diseñadas para optimizar la gestión de tu empresa
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
                className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-500"
              >
                <div className="text-purple-400 mb-4">{feature.icon}</div>
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-400">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Plans */}
      <section className="py-20 px-4 bg-gradient-to-b from-black to-gray-900">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Planes a tu medida
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a las necesidades de tu negocio
            </p>
          </motion.div>

          <div className="max-w-6xl mx-auto space-y-10">
            {FREE_PLAN ? (
              <FreePlanRow plan={FREE_PLAN.name} price={FREE_PLAN.price} features={FREE_PLAN.features} delay={0.05} />
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {PAID_PLANS.map((plan, index) => (
                <PlanCard
                  key={index}
                  plan={plan.name}
                  price={plan.price}
                  features={plan.features}
                  popular={(plan as any).popular}
                  delay={index * 0.15}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Conecta con tus herramientas
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Integra POS Modular con las plataformas que ya usas
            </p>
          </motion.div>

          <div className="relative">
            <div className="flex overflow-hidden py-4">
              <motion.div
                className="flex gap-8"
                animate={{ x: [0, -1030] }}
                transition={{
                  x: {
                    repeat: Infinity,
                    repeatType: 'loop',
                    duration: 20,
                    ease: 'linear',
                  },
                }}
              >
                {INTEGRATIONS.map((integration, index) => (
                  <IntegrationLogo key={index} name={integration} />
                ))}
                {INTEGRATIONS.map((integration, index) => (
                  <IntegrationLogo key={`dup-${index}`} name={integration} />
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 bg-gradient-to-b from-gray-900 to-black">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Lo que dicen nuestros clientes
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Descubre cómo POS Modular está transformando negocios
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
              >
                <TestimonialCard {...TESTIMONIALS[activeTestimonial]} />
              </motion.div>
            </AnimatePresence>

            <div className="flex justify-center mt-8 gap-2">
              {TESTIMONIALS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`h-3 rounded-full transition-all duration-300 ${
                    index === activeTestimonial
                      ? 'bg-gradient-to-r from-purple-500 to-blue-500 w-8'
                      : 'bg-gray-700 w-3'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="relative rounded-3xl p-8 md:p-12 bg-gradient-to-br from-purple-900/30 to-blue-900/30 border border-purple-500/30 backdrop-blur-xl"
          >
            <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-lg opacity-30" />
            <div className="relative z-10">
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Empieza hoy con POS Modular
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Únete a miles de negocios que ya están optimizando sus operaciones con nuestro
                sistema.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => router.push('/venta')}
                className="px-10 py-4 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl font-semibold text-lg shadow-lg shadow-purple-500/30 flex items-center gap-2 mx-auto"
              >
                Comenzar Prueba Gratuita <ArrowRight className="h-5 w-5" />
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/80 backdrop-blur-xl border-t border-white/10 py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-blue-500 bg-clip-text text-transparent mb-4">
                POS Modular
              </h3>
              <p className="text-gray-400 max-w-md">
                El sistema de punto de venta más completo y moderno para impulsar tu negocio hacia
                el futuro.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Enlaces</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Inicio
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Funcionalidades
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Planes
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Contacto
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-4">Legal</h4>
              <ul className="space-y-2">
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Términos de Servicio
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Política de Privacidad
                  </a>
                </li>
                <li>
                  <a href="#" className="text-gray-400 hover:text-white transition-colors">
                    Cookies
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/10 mt-8 pt-8 text-center text-gray-500">
            <p>© 2025 POS Modular — Tecnología para tu negocio</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default POSModularLanding;
